import { nextTick, ref, shallowRef, type Ref } from 'vue';
import { BookCore } from '@/modules/flipcore/BookCore';
import { ReaderPageLoadManager } from '@/modules/flipcore/ReaderPageLoadManager';
import type { BookCanvas } from '@/modules/editor/canvas/BookCanvas.class';
import { useProjectStore } from '@/store/project';
import { textureManager } from '@/managers/TextureManager';
import { loadPublishedReaderProject } from '@/modules/editor/canvas/models/template/readerProjectLoader';
import { emitter } from '@/utils/emitter';
import { debugCheckpoint } from '@/utils/mobileDebugConsole';
import EVENT_NAMES from '@/constants/eventName';
import type { BoundFlipAttemptEvent, DisplayMode } from '@/types/book';
import type { BookData } from '@/types/canvas-type';
import { isLandscapeDoublePageLayout } from './useReaderLandscape';

const MOBILE_TEXTURE_BUDGET_BYTES = 64 * 1024 * 1024;
const PC_TEXTURE_BUDGET_BYTES = 128 * 1024 * 1024;
const READER_TEXTURE_LOAD_CONCURRENCY = 3;

interface UseReaderBookOptions {
  /** 嵌入（编辑器预览）模式：复用已加载的 projectStore 数据，走事件重建路径。 */
  embedded: boolean;
  isPC: Ref<boolean>;
  isLandscape: Ref<boolean>;
  currentPageIndex: Ref<number>;
  activeDisplayMode: Ref<DisplayMode>;
  getCanvas: () => BookCanvas | undefined;
  getProjectId: () => string | undefined;
  getPageDisplaySize?: (
    book: BookData,
    displayMode: DisplayMode
  ) => { width: number; height: number } | null;
  /** 书本翻页后回调（预加载在内部先做完），外部用于续播等联动。 */
  onPageChange?: (pageIndex: number) => void;
  /** 用户朝首末页边界外继续翻页时回调。 */
  onBoundFlipAttempt?: (event: BoundFlipAttemptEvent) => void;
  /** timeline 播放到末页完成回调。 */
  onTimelineComplete?: () => void;
  onPageErrorEdit?: () => void;
  canPageErrorEdit?: () => boolean;
  getPageErrorEditLabel?: () => string;
  /** 新书创建完成后回调，外部用于同步布局 / 音频 / 缩放监听。 */
  onBookCreated?: (book: BookCore, displayMode: DisplayMode) => void;
  /** 移动端方向清晰重绘更新几何后，同步提交基础 scale + position。 */
  onBookDisplayResize?: (book: BookCore, displayMode: DisplayMode) => void;
  /** 首屏可见页资源加载完成。 */
  onBookReady?: () => void;
  /** 销毁当前书本前回调，外部用于复位缩放 / 续播等外部状态。 */
  onBeforeDestroy?: () => void;
}

/**
 * 书本生命周期域：建/毁书、加载（CDN 或编辑器预览事件）、
 * 由 ReaderPageLoadManager 按可视窗口统一加载插画+旁白。
 */
export const useReaderBook = (options: UseReaderBookOptions) => {
  const {
    embedded,
    isPC,
    isLandscape,
    currentPageIndex,
    activeDisplayMode,
    getCanvas,
    getProjectId,
    getPageDisplaySize,
    onPageChange,
    onBoundFlipAttempt,
    onTimelineComplete,
    onPageErrorEdit,
    canPageErrorEdit,
    getPageErrorEditLabel,
    onBookCreated,
    onBookDisplayResize,
    onBookReady,
    onBeforeDestroy
  } = options;

  const projectStore = useProjectStore();

  const currentBook = shallowRef<BookCore | null>(null);
  let pageLoadManager: ReaderPageLoadManager | null = null;

  /** 工程数据与书本骨架未就绪时显示全局 loading；页面内容使用页内 loading 异步加载 */
  const isBookLoading = ref(!embedded);

  const getCurrentBook = () => currentBook.value;

  /** 强制横屏版式、PC 与真实横屏视口使用双页；其余移动端竖屏使用单页。 */
  const getDisplayMode = (): DisplayMode => {
    if (isLandscapeDoublePageLayout(projectStore.layout)) return 'double';
    return isPC.value || isLandscape.value ? 'double' : 'single';
  };

  const syncPageIndex = () => {
    const book = currentBook.value;
    if (!book) return;

    currentPageIndex.value = book.currentPageIndex;
  };

  const runBookAction = (action: (book: BookCore) => void) => {
    const book = currentBook.value;
    if (!book) return;

    action(book);
    window.requestAnimationFrame(syncPageIndex);
  };

  const prev = () => runBookAction((book) => book.prev('bottom', 'user'));
  const next = () => runBookAction((book) => book.next('bottom', 'user'));

  /** Gate：只等当前跨页就绪；完整窗口由 sync 后台预加载。 */
  const waitSpreadReady = async (pageIndex: number) => {
    await pageLoadManager?.waitForSpread(pageIndex);
  };

  const createBook = async (previewBook: BookData) => {
    if (!previewBook.pages) return null;

    if (!embedded) {
      textureManager.configureReaderSession({
        budgetBytes: isPC.value ? PC_TEXTURE_BUDGET_BYTES : MOBILE_TEXTURE_BUDGET_BYTES,
        maxConcurrent: READER_TEXTURE_LOAD_CONCURRENCY
      });
    }

    const displayMode = getDisplayMode();
    activeDisplayMode.value = displayMode;
    const pageErrorEditEnabled = canPageErrorEdit?.() ?? true;
    const canvas = getCanvas();
    if (!canvas) return null;
    const sourcePage = previewBook.pages[0];
    const sourcePageWidth = previewBook.width ?? sourcePage?.width ?? 0;
    const sourcePageHeight = previewBook.height ?? sourcePage?.height ?? 0;
    const displaySize = getPageDisplaySize?.(previewBook, displayMode);
    const pageWidth = displaySize?.width ?? sourcePageWidth;
    const pageHeight = displaySize?.height ?? sourcePageHeight;
    const radiusScale = sourcePageWidth > 0 ? pageWidth / sourcePageWidth : 1;

    canvas.setPageDisplaySize(pageWidth, pageHeight);
    previewBook.displayWidth = pageWidth * (displayMode === 'double' ? 2 : 1);
    previewBook.displayHeight = pageHeight;

    const book: BookCore = new BookCore({
      resolution: 1,
      pageWidth,
      pageHeight,
      pageRadius: (previewBook.pageRadius ?? 20) * radiusScale,
      displayMode,
      coverFlipType: previewBook.coverFlipType,
      onPageErrorEdit: pageErrorEditEnabled ? onPageErrorEdit : undefined,
      onPageErrorRetry: () => pageLoadManager?.retryVisibleFailures(),
      pageErrorEditLabel: getPageErrorEditLabel?.(),
      onPageChange: ({ pageIndex }) => {
        currentPageIndex.value = pageIndex;
        // 统一推进可视窗口（插画 + 旁白），后方页后台预加载
        pageLoadManager?.sync(pageIndex);
      },
      onBoundFlipAttempt
    });

    await canvas.addBook(book);
    previewBook.pages.forEach((page, index) => {
      book.setPageNeedSpineShadow(index, page.needSpineShadow !== false);
    });

    onBookCreated?.(book, displayMode);

    const timeline = canvas.getTimeline();
    if (timeline) {
      timeline.onComplete = () => onTimelineComplete?.();
    }

    pageLoadManager?.dispose();
    pageLoadManager = new ReaderPageLoadManager(book, canvas, {
      displayMode
    });
    canvas.setPageChangeReadyHandler(waitSpreadReady);
    canvas.setPageChangeReadyChecker(
      (pageIndex) => pageLoadManager?.isSpreadReady(pageIndex) ?? false
    );
    canvas.setPageChangeSettledHandler(onPageChange ?? null);
    pageLoadManager.sync(book.currentPageIndex);

    return book;
  };

  let pendingDisplaySize: {
    width: number;
    height: number;
    displayMode: DisplayMode;
    version: number;
  } | null = null;
  let displayResizeTask: Promise<void> | null = null;
  let displayResizeVersion = 0;
  let displayPagesInvalidated = false;

  /** 仅在移动端单双页模式变化时，按稳定后的最终像素尺寸清晰重建一次。 */
  const resizeBook = async (
    width: number,
    height: number,
    displayMode: DisplayMode
  ): Promise<void> => {
    if (width <= 0 || height <= 0) return;
    const version = ++displayResizeVersion;
    pendingDisplaySize = { width, height, displayMode, version };
    if (displayResizeTask) return displayResizeTask;

    displayResizeTask = (async () => {
      while (pendingDisplaySize) {
        const nextSize = pendingDisplaySize;
        pendingDisplaySize = null;

        const book = currentBook.value;
        const loadManager = pageLoadManager;
        const canvas = getCanvas();
        if (!book || !loadManager || !canvas) continue;
        const previousDisplayMode = book.displayMode;
        if (
          book.pageWidth === nextSize.width &&
          book.pageHeight === nextSize.height &&
          book.displayMode === nextSize.displayMode &&
          !displayPagesInvalidated
        )
          continue;

        displayPagesInvalidated = true;
        const resized = await loadManager.resizePages(nextSize.displayMode, () => {
          if (
            nextSize.version !== displayResizeVersion ||
            currentBook.value !== book ||
            pageLoadManager !== loadManager
          )
            return false;

          canvas.setPageDisplaySize(nextSize.width, nextSize.height);
          book.resize(nextSize.width, nextSize.height, nextSize.displayMode);
          activeDisplayMode.value = nextSize.displayMode;
          currentPageIndex.value = book.currentPageIndex;
          onBookDisplayResize?.(book, nextSize.displayMode);
          projectStore.book.displayWidth =
            nextSize.width * (nextSize.displayMode === 'double' ? 2 : 1);
          projectStore.book.displayHeight = nextSize.height;
          return true;
        });
        if (resized && previousDisplayMode !== nextSize.displayMode) {
          await canvas.syncFlipActionsForDisplayMode();
        }
        if (resized) displayPagesInvalidated = false;
      }
    })().finally(() => {
      displayResizeTask = null;
    });

    return displayResizeTask;
  };

  const destroyCurrentBook = async () => {
    displayResizeVersion += 1;
    pendingDisplaySize = null;
    displayPagesInvalidated = false;
    onBeforeDestroy?.();
    pageLoadManager?.dispose();
    pageLoadManager = null;
    currentBook.value?.setInteractionEnabled(true);
    getCanvas()?.setPageChangeReadyHandler(null);
    getCanvas()?.setPageChangeReadyChecker(null);
    getCanvas()?.setPageChangeSettledHandler(null);
    getCanvas()?.removeBook();
    currentBook.value?.destroy();
    currentBook.value = null;
    if (!embedded) await textureManager.endReaderSession();
  };

  /** 等待 Vue 刷 DOM，并再等两帧确保浏览器已画出 loading 遮罩。 */
  const waitForLoadingPaint = async () => {
    // 1. 先让 Vue 把 v-if 的 loading 节点挂到 DOM
    await nextTick();
    // 2. 双 rAF：第一帧提交绘制，第二帧确认 paint 完成后再继续重活，避免遮罩被同步任务堵住
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => resolve());
      });
    });
  };

  const initBook = async () => {
    // 1. 立刻打开 loading，并让出主线程先画出遮罩
    isBookLoading.value = true;
    await waitForLoadingPaint();

    // 2. 再销毁旧书并加载新数据（此前的同步销毁会堵住首帧绘制）
    await destroyCurrentBook();

    try {
      if (!embedded) {
        // 有 id（工作台按 id 预览）走 CDN；无 id（阅读端独立部署）读同级 project-config.json
        const projectId = getProjectId();
        if (import.meta.env.APP === 'reader') {
          await loadPublishedReaderProject();
        } else {
          const { projectManager } = await import('@/managers/ProjectManager');
          await projectManager.open(projectId, { showLoading: false });
        }
        currentBook.value = await createBook(projectStore.book);
        syncPageIndex();
      } else {
        await new Promise<void>((resolve, reject) => {
          emitter.emit(EVENT_NAMES.PROJECT_PREVIEW_LOAD, {
            resolve: async () => {
              try {
                currentBook.value = await createBook(projectStore.book);
                syncPageIndex();
                resolve();
              } catch (error) {
                reject(error);
              }
            },
            reject
          });
        });
      }

      const book = currentBook.value;
      const loadManager = pageLoadManager;
      if (book && loadManager) {
        // 不阻塞全局 loading；当前跨页完成后再启动依赖页面内容的音频与布局逻辑。
        void loadManager.waitForSpread(book.currentPageIndex).then(() => {
          if (currentBook.value !== book || pageLoadManager !== loadManager) return;
          onBookReady?.();
        });
      }
    } catch (error) {
      debugCheckpoint('reader', 'initBook:error', error);
      console.error(error);
    } finally {
      isBookLoading.value = false;
    }
  };

  /** 用当前 projectStore 数据重建书本，供编辑器预览弹窗每次打开时调用。 */
  const refresh = async () => {
    await initBook();
  };

  return {
    getCurrentBook,
    currentPageIndex,
    activeDisplayMode,
    isBookLoading,
    getDisplayMode,
    prev,
    next,
    syncPageIndex,
    resizeBook,
    initBook,
    refresh,
    destroy: destroyCurrentBook
  };
};
